export interface TourStep {
  id: string;
  stepNumber: number;
  tab: 'data' | 'design' | 'imposition' | 'production';
  title: string;
  category: string;
  categoryColor: 'blue' | 'emerald' | 'amber' | 'purple' | 'indigo' | 'rose' | 'cyan';
  description: string;
  targetSelector: string; // CSS selector or 'center'
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  keyHighlights: string[];
  actionLabel?: string;
  actionType?: 'load_sample_data' | 'add_qr' | 'open_3d' | 'auto_optimize' | 'switch_tab';
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    stepNumber: 1,
    tab: 'data',
    title: 'Welcome to Variable Sticker Studio',
    category: 'Getting Started',
    categoryColor: 'indigo',
    description: 'Yeh interactive guided walkthrough aapko data import se lekar dynamic design create karne, imposition configure karne, ROI check karne aur final print-ready PDF export tak ka complete workflow sikhayega.',
    targetSelector: 'center',
    placement: 'center',
    keyHighlights: [
      'End-to-end Variable Data Printing (VDP) SaaS workstation',
      '4 structured linear production steps',
      'Press imposition, cutting plotters & 3D virtual proofing',
      'Use Arrow keys (Left/Right) or buttons to navigate'
    ]
  },
  {
    id: 'data-import',
    stepNumber: 2,
    tab: 'data',
    title: '1. Data Import & Sample Datasets',
    category: 'Step 1: Data Setup',
    categoryColor: 'blue',
    description: 'Yahan se aap apna CSV/Excel spreadsheet upload kar sakte hain ya instant 1-click industry sample datasets load kar sakte hain. System automatically delimiters (comma, tab, semicolon) aur header columns ko detect karta hai.',
    targetSelector: '#tour-data-upload',
    placement: 'bottom',
    keyHighlights: [
      'Drag-and-drop CSV, TSV, or TXT spreadsheets',
      'Automatic delimiter sniffing & header row detection',
      'Instant pre-built datasets: E-Commerce, Event Passes, Asset Tags'
    ],
    actionLabel: 'Load Sample E-Commerce Data',
    actionType: 'load_sample_data'
  },
  {
    id: 'data-generator',
    stepNumber: 3,
    tab: 'data',
    title: '2. In-App Variable Generator',
    category: 'Step 1: Data Setup',
    categoryColor: 'blue',
    description: 'Agar aapke paas spreadsheet nahi hai, toh aap in-app generator se sequential serial numbers (SN-0001, SN-0002...), EAN-13 barcodes, dates, ya random security codes directly create kar sakte hain.',
    targetSelector: '#tour-sample-datasets',
    placement: 'top',
    keyHighlights: [
      'Generate thousands of sequential or alphanumeric records',
      'ISO/IEC standard checksums for EAN-13 & UPC-A barcodes',
      'Live spreadsheet grid preview with column validation'
    ]
  },
  {
    id: 'sidebar-nav',
    stepNumber: 4,
    tab: 'data',
    title: '3. Production Pipeline Navigation',
    category: 'Workflow Pipeline',
    categoryColor: 'purple',
    description: 'Sidebar mein char (4) primary production stages hain: Data Source, Template Builder, Imposition Layout, aur Production Export. Step 2 (Template Builder) par chalkar label design banate hain!',
    targetSelector: '#tour-sidebar-steps',
    placement: 'right',
    keyHighlights: [
      '1. Data Source: Manage records & variable columns',
      '2. Template Builder: Canvas, dynamic elements & 3D proofing',
      '3. Imposition Layout: Multi-up sheets, spacing & crop marks',
      '4. Production Export: Cost ROI, job tickets & print PDFs'
    ],
    actionLabel: 'Go to Template Builder →',
    actionType: 'switch_tab'
  },
  {
    id: 'canvas-dimensions',
    stepNumber: 5,
    tab: 'design',
    title: '4. Label Dimensions & Shape Setup',
    category: 'Step 2: Template Builder',
    categoryColor: 'emerald',
    description: 'Apne sticker ka exact size (width, height) set karein mm, inches, cm ya points mein. Rectangle, Rounded Corner (die-cut), Circle ya Oval shape choose karein aur background color select karein.',
    targetSelector: '#tour-template-dimension-bar',
    placement: 'right',
    keyHighlights: [
      'Millimeter & inch precision with physical print scaling',
      'Die-cut shapes: Rounded corners, circles, and ovals',
      'Common preset buttons (2x2", 3x3", 4x6" label, 50mm²)'
    ]
  },
  {
    id: 'insert-elements',
    stepNumber: 6,
    tab: 'design',
    title: '5. Insert Dynamic Elements',
    category: 'Step 2: Template Builder',
    categoryColor: 'emerald',
    description: 'Toolbar se dynamic elements add karein: Dynamic Variable Text, QR Codes, 1D/2D Barcodes (Code128, EAN-13, DataMatrix), Serial Number badges, Images/Logos, aur Vector Shapes.',
    targetSelector: '#tour-element-tools',
    placement: 'right',
    keyHighlights: [
      'Dynamic text bound to spreadsheet records',
      'High-resolution vector QR codes & industry barcodes',
      'Brand logos, icons, and geometric frame shapes'
    ],
    actionLabel: 'Insert Sample QR Code',
    actionType: 'add_qr'
  },
  {
    id: 'canvas-workspace',
    stepNumber: 7,
    tab: 'design',
    title: '6. Interactive Canvas & Snapping',
    category: 'Step 2: Template Builder',
    categoryColor: 'emerald',
    description: 'Artboard par elements ko click karke move, resize, rotate aur align karein. Rulers, guidelines, magnetic smart grid snapping (10px), aur bleed safety margins ensure karte hain ki print perfect ho.',
    targetSelector: '#tour-design-canvas',
    placement: 'bottom',
    keyHighlights: [
      'Interactive resize, drag, and rotation handles',
      'Magnetic snap-to-grid & center alignment guides',
      'Undo / Redo history support (Ctrl+Z / Ctrl+Y)'
    ]
  },
  {
    id: 'properties-binding',
    stepNumber: 8,
    tab: 'design',
    title: '7. Properties & Variable Data Binding',
    category: 'Step 2: Template Builder',
    categoryColor: 'emerald',
    description: 'Right sidebar mein selected element ki properties customize karein: Spreadsheet column binding (jaise {{Product_Name}} ya {{Barcode}}), fonts, colors, text alignment, barcode quiet zones, aur opacity.',
    targetSelector: '#tour-properties-panel',
    placement: 'left',
    keyHighlights: [
      '1-click column binding: Link elements to dynamic CSV columns',
      'Typography controls: Font family, size, line-height & tracking',
      'Barcode formatting: Symbology, check digits & error correction'
    ]
  },
  {
    id: 'threed-proof',
    stepNumber: 9,
    tab: 'design',
    title: '8. 3D Packaging Virtual Proof Studio',
    category: 'Virtual Proofing',
    categoryColor: 'indigo',
    description: 'Print karne se pehle sticker ko 3D packaging par realistic orbit view mein inspect karein! Glass bottles, aluminum soda cans, pouches, boxes aur jars par metallic foil, spot varnish aur holographic finishes check karein.',
    targetSelector: '#tour-btn-3d-proof',
    placement: 'bottom',
    keyHighlights: [
      'Full 360° interactive turntable orbit and zoom',
      'Hot foil stamping (Gold, Silver), velvet matte, and gloss varnish',
      'Download high-res 3D proof renders for client approval'
    ],
    actionLabel: 'Open 3D Proof Studio',
    actionType: 'open_3d'
  },
  {
    id: 'imposition-page',
    stepNumber: 10,
    tab: 'imposition',
    title: '9. Press Media & Unified Imposition Bar',
    category: 'Step 3: Imposition Setup',
    categoryColor: 'amber',
    description: 'Step 3 Imposition toolbar mein sabhi essential controls ek hi sleek row mein icons aur tooltips ke saath integrated hain: Properties drawer (Presets, SRA3/A3+ sheet sizes, orientation, gripper margins), live grid thumbnail, aur real-time ROI paper economics.',
    targetSelector: '#tour-imposition-toolbar',
    placement: 'bottom',
    keyHighlights: [
      'Single unified top bar with tooltips saving vertical canvas height',
      'Instant slide-over drawer for commercial press media presets',
      'Real-time substrate efficiency & paper savings indicator'
    ]
  },
  {
    id: 'imposition-layout',
    stepNumber: 11,
    tab: 'imposition',
    title: '10. N-Up Grid & Auto-Nest Optimizer',
    category: 'Step 3: Imposition Setup',
    categoryColor: 'amber',
    description: 'Rows (R), Columns (C), Gaps (X/Y), aur Margins (X/Y) directly adjust karein. "Auto-Calculate" (Zap icon) par click karke sheet par maximum stickers calculate karein, ya Center icon se grid ko sheet par equalize karein.',
    targetSelector: '#tour-imposition-grid-controls',
    placement: 'bottom',
    keyHighlights: [
      'Auto-Nest Optimizer (Zap icon): Maximum stickers yield calculation',
      'Direct Rows, Columns, Spacing Gaps, and Sheet Margins inputs',
      'Center Grid & 90° Sticker Rotation single-click shortcuts'
    ],
    actionLabel: 'Run Auto-Optimize Grid',
    actionType: 'auto_optimize'
  },
  {
    id: 'imposition-marks',
    stepNumber: 12,
    tab: 'imposition',
    title: '11. Prepress Tools, Marks & 3D Proofing',
    category: 'Step 3: Imposition Setup',
    categoryColor: 'amber',
    description: 'Prepress controls enable karein: Safe Zone visual guide, CMYK density color bars, Trim/Media Box inspector, Cutting Plotter registration marks (ARMS/OPOS), Multi-Job Ganging, aur 3D Packaging Mockup Studio.',
    targetSelector: '#tour-imposition-prepress-tools',
    placement: 'bottom',
    keyHighlights: [
      'Interactive Safe Zone & inner margin guides',
      'CMYK density calibration color bars & trim marks',
      'Plotter registration marks & direct 3D Packaging Virtual Proofing'
    ]
  },
  {
    id: 'production-roi',
    stepNumber: 13,
    tab: 'production',
    title: '12. Production ROI & Material Economics',
    category: 'Step 4: Production & ROI',
    categoryColor: 'rose',
    description: 'Substrate utilization percentage, total sheet waste, estimated ink cost, press run duration, aur total savings analyze karein. Multi-up imposition se kitne master sheets aur printing cost ki bachat hoti hai yahan live calculate hota hai.',
    targetSelector: '#tour-production-roi',
    placement: 'top',
    keyHighlights: [
      'Substrate efficiency gauge: Track paper waste percentage',
      'Multi-currency cost breakdown: Substrate, ink, and operator labor',
      'Material savings compared to single-sheet printing'
    ]
  },
  {
    id: 'production-ticket',
    stepNumber: 14,
    tab: 'production',
    title: '13. Commercial Job Ticket (JDF/JSON)',
    category: 'Step 4: Production & ROI',
    categoryColor: 'rose',
    description: 'Press operator aur client ke liye official Job Docket generate karein. Isme unique Work Order number, substrate GSM, total sheets required, cut contour specifications, aur operator instructions include hote hain.',
    targetSelector: '#tour-btn-job-ticket',
    placement: 'top',
    keyHighlights: [
      'Standardized job docket with unique Work Order ID',
      'Complete pre-press checklist and press operator notes',
      'Downloadable JDF / JSON machine-readable production spec'
    ]
  },
  {
    id: 'production-pdf',
    stepNumber: 15,
    tab: 'production',
    title: '14. High-Res Print-Ready PDF Export',
    category: 'Step 4: Production & ROI',
    categoryColor: 'rose',
    description: 'Sabhi variable records ko merge karke final print-ready PDF export karein. 300/600 DPI vector barcodes, true fonts, crop marks, imposed layout sheets direct digital press par print hone ke liye ready hain.',
    targetSelector: '#tour-btn-export-pdf',
    placement: 'top',
    keyHighlights: [
      'Multi-record batch generation with live progress HUD',
      'Vector sharp barcodes & QR codes with zero rasterization',
      'Single master PDF or individual record sheets'
    ]
  },
  {
    id: 'completed',
    stepNumber: 16,
    tab: 'production',
    title: 'Tour Completed - You Are Ready to Print!',
    category: 'Completed',
    categoryColor: 'emerald',
    description: 'Badhai ho! Aapne Variable Sticker Studio ka complete workflow seekh liya hai. Top navigation bar mein "Product Tour" button par click karke aap kisi bhi waqt is walkthrough ko dobara access kar sakte hain.',
    targetSelector: 'center',
    placement: 'center',
    keyHighlights: [
      'Linear workflow: Data Source → Template Builder → Imposition → Production',
      'Press "?" on your keyboard anytime to view shortcuts cheatsheet',
      'Automated auto-save protects your templates & layout settings'
    ]
  }
];
